/**
 * AiHelpSessionList - Conversazioni precedenti con l'assistente, dalla piu' recente.
 * Stesso stile del pannello chat: grigi e nero, righe semplici, elimina al passaggio.
 */

import { useEffect, type MouseEvent } from 'react';
import { Plus, Trash2, Loader2, X, ArrowLeft } from 'lucide-react';
import { useAiHelpStore } from '../../lib/stores/aiHelpStore';
import { formatDistanceToNow } from 'date-fns';
import { it } from 'date-fns/locale';

interface AiHelpSessionListProps {
  onClose: () => void;
}

const iconBtn = 'p-1.5 rounded-md text-[#787774] hover:bg-[rgba(55,53,47,0.08)] hover:text-[#37352F] dark:hover:text-white transition-colors';

export function AiHelpSessionList({ onClose }: AiHelpSessionListProps) {
  const { sessions, loadingSessions, fetchSessions, startNewChat, selectSession, deleteSession, setWidgetState } = useAiHelpStore();
  const top = 'top-12';

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const open = async (id: string) => { await selectSession(id); setWidgetState('chat'); };
  const remove = async (e: MouseEvent, id: string) => { e.stopPropagation(); await deleteSession(id); };

  return (
    <aside aria-label="Conversazioni con l'assistente"
      className={`fixed ${top} right-0 bottom-0 z-[56] w-[420px] max-w-full bg-white dark:bg-gray-900 flex flex-col overflow-hidden border-l border-[rgba(55,53,47,0.09)] dark:border-gray-700 shadow-[-4px_0_16px_rgba(0,0,0,0.06)] animate-[slideInRight_0.25s_ease-out]`}>
      <header className="flex items-center gap-2 px-3 h-12 border-b border-[rgba(55,53,47,0.09)] dark:border-gray-700 flex-shrink-0">
        <button type="button" className={iconBtn} onClick={() => setWidgetState('chat')} title="Torna alla chat" aria-label="Torna alla chat"><ArrowLeft className="w-4 h-4" strokeWidth={1.75} /></button>
        <p className="flex-1 text-sm font-semibold text-[#37352F] dark:text-white">Conversazioni</p>
        <button type="button" className={iconBtn} onClick={() => startNewChat()} title="Nuova chat" aria-label="Nuova chat"><Plus className="w-4 h-4" strokeWidth={1.75} /></button>
        <button type="button" className={iconBtn} onClick={onClose} title="Chiudi" aria-label="Chiudi"><X className="w-4 h-4" strokeWidth={1.75} /></button>
      </header>

      <div className="flex-1 overflow-y-auto p-2">
        {loadingSessions ? (
          <div className="flex items-center justify-center h-full"><Loader2 className="w-5 h-5 animate-spin text-[#9B9A97]" /></div>
        ) : sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full gap-3 px-6 text-center">
            <p className="text-sm text-[#787774]">Nessuna conversazione ancora.</p>
            <button type="button" onClick={() => startNewChat()} className="px-3 py-1.5 text-sm font-medium text-white rounded-md bg-[#37352F] hover:bg-[#2f2e2a]">Nuova chat</button>
          </div>
        ) : (
          <ul className="space-y-0.5">
            {sessions.map((s) => (
              <li key={s.id}>
                <div role="button" tabIndex={0} onClick={() => open(s.id)} onKeyDown={(e) => { if (e.key === 'Enter') open(s.id); }}
                  className="group flex items-center gap-2 px-3 py-2 rounded-md cursor-pointer hover:bg-[rgba(55,53,47,0.05)]">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm text-[#37352F] dark:text-gray-100 truncate">{s.title}</p>
                    <p className="text-[11px] text-[#9B9A97]">{formatDistanceToNow(new Date(s.updated_at), { addSuffix: true, locale: it })}</p>
                  </div>
                  <button type="button" onClick={(e) => remove(e, s.id)} aria-label={`Elimina ${s.title}`}
                    className="p-1 rounded opacity-0 group-hover:opacity-100 focus:opacity-100 text-[#9B9A97] hover:text-red-600 hover:bg-[rgba(55,53,47,0.08)]">
                    <Trash2 className="w-3.5 h-3.5" strokeWidth={1.75} />
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </aside>
  );
}
